import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { motion } from 'motion/react';
import SEO from '../components/SEO';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import EbookDownloadModal from '../components/EbookDownloadModal';

import {
  ArrowRight,
  BookOpen,
  Calendar,
  DownloadSimple,
  FileText
} from '@phosphor-icons/react';
const Resources = () => {
  const [blogs, setBlogs] = useState([]);
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedEbook, setSelectedEbook] = useState(null);

  const springConfig = {
    type: 'spring',
    damping: 80,
    stiffness: 400,
    mass: 1,
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const [blogsRes, ebooksRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/blogs`),
        fetch(`${BACKEND_URL}/api/ebooks`),
      ]);

      const blogsData = await blogsRes.json();
      const ebooksData = await ebooksRes.json();

      setBlogs(blogsData);
      setEbooks(ebooksData);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadClick = (ebook) => {
    setSelectedEbook(ebook);
    setShowDownloadModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="bg-[#161B26]">
      <SEO 
        title="Resources - Industry Insights, Guides & Ebooks | Man & Machine"
        description="Access expert insights on AI, business intelligence, and digital transformation. Download free ebooks, read industry blogs, and stay ahead with our comprehensive guides and resources."
        keywords="AI guides, business intelligence ebooks, digital transformation resources, marketing automation guides, AI implementation, business strategy resources"
        canonicalUrl="https://manmachine.agency/resources"
      />
      <LandingHeader />
      
      {/* Hero Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-20"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-20"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="display-hero select-none text-white mb-6">
              Industry <span className="text-[#6366F1]">Insights & Resources</span>
            </h1>
            <p className="body-large text-[#A0AEC0] max-w-3xl mx-auto mb-4">
              Practical guides, real-world playbooks, and expert insights to help businesses implement AI with confidence — not hype.
            </p>
            <p className="text-[#718096] text-sm">
              Built by operators, not theorists. Everything here is designed to be applied.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ebooks Section */}
      <section id="ebooks" className="py-20 bg-[#161B26]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-[rgba(236,72,153,0.1)]">
                <DownloadSimple size={32} className="text-[#EC4899]" />
              </div>
              <h2 className="heading-large select-none text-white">Downloadable Guides</h2>
            </div>
            <p className="body-large text-[#A0AEC0]">
              In-depth ebooks and frameworks designed to help you identify, prioritize, and implement AI opportunities across your business.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ebooks.length === 0 && !loading && (
              <div className="col-span-full">
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={springConfig}
                  className="glass rounded-2xl p-12 text-center"
                >
                  <div className="w-20 h-20 rounded-full bg-[rgba(236,72,153,0.1)] flex items-center justify-center mx-auto mb-6">
                    <DownloadSimple size={40} className="text-[#EC4899]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    Our first Man & Machine guide is launching soon.
                  </h3>
                  <p className="text-[#A0AEC0] mb-6">
                    Join the list to be notified when we publish our first comprehensive AI opportunities framework.
                  </p>
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all"
                  >
                    Get Notified
                    <ArrowRight size={16} />
                  </a>
                </motion.div>
              </div>
            )}
            {ebooks.map((ebook, index) => (
              <motion.div
                key={ebook.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springConfig, delay: index * 0.1 }}
                className="glass rounded-2xl overflow-hidden hover:bg-[rgba(255,255,255,0.08)] transition-all hover-lift card-glow group"
              >
                <div className="aspect-[3/4] overflow-hidden relative">
                  <img
                    src={`${BACKEND_URL}/api${ebook.cover_image || ebook.image_url}`}
                    alt={ebook.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-[var(--surface-0)] opacity-60"></div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-[#718096] mb-3">
                    <span>{ebook.pages} pages</span>
                    <span>•</span>
                    <span>{ebook.format}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#EC4899] transition-colors">
                    {ebook.title}
                  </h3>
                  <p className="body-standard text-[#A0AEC0] mb-4 line-clamp-3">
                    {ebook.description?.length > 150 
                      ? `${ebook.description.substring(0, 150)}...` 
                      : ebook.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {(ebook.topics || []).map((topic) => (
                      <span
                        key={topic}
                        className="px-2 py-1 rounded-md text-xs bg-[rgba(99,102,241,0.1)] text-[#6366F1] border border-[rgba(99,102,241,0.2)]"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                  <button 
                    onClick={() => handleDownloadClick(ebook)}
                    className="w-full rounded-full bg-[#6366F1] hover:bg-[#5558E6] disabled:opacity-50 disabled:cursor-not-allowed mb-3 transition-colors"
                    disabled={!ebook.file_path}
                  >
                    <span className="flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold text-sm">
                      {ebook.file_path ? 'Download Free Guide' : 'Coming Soon'}
                      {ebook.file_path && <DownloadSimple size={16} />}
                    </span>
                  </button>
                  <p className="text-xs text-center text-[#718096]">
                    Free download · No spam · Unsubscribe anytime
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Blogs Section */}
      <section id="blogs" className="py-20 bg-[#12141A]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-[rgba(99,102,241,0.1)]">
                <BookOpen size={32} className="text-[#6366F1]" />
              </div>
              <h2 className="heading-large select-none text-white">Latest Articles</h2>
            </div>
            <p className="body-large text-[#A0AEC0]">
              Short, practical articles covering AI strategy, automation, operations, and growth — written for founders, operators, and consultants.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogs.length === 0 && !loading && (
              <div className="col-span-full">
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={springConfig}
                  className="glass rounded-2xl p-12 text-center"
                >
                  <div className="w-20 h-20 rounded-full bg-[rgba(99,102,241,0.1)] flex items-center justify-center mx-auto mb-6">
                    <BookOpen size={40} className="text-[#6366F1]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    Articles are coming soon.
                  </h3>
                  <p className="text-[#A0AEC0]">
                    We're publishing our first insights shortly.
                  </p>
                </motion.div>
              </div>
            )}
            {blogs.map((blog, index) => (
              <motion.article
                key={blog.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springConfig, delay: index * 0.1 }}
                className="glass rounded-2xl overflow-hidden hover:bg-[rgba(255,255,255,0.08)] transition-all hover-lift card-glow group cursor-pointer"
                onClick={() => window.location.href = `/blog/${blog.id}`}
              >
                <div className="aspect-video overflow-hidden relative bg-[rgba(99,102,241,0.1)]">
                  <img
                    src={
                      (blog.hero_image || blog.image_url)?.startsWith('http') 
                        ? (blog.hero_image || blog.image_url)
                        : `${BACKEND_URL}${blog.hero_image || blog.image_url}`
                    }
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#6366F1] text-white">
                      {blog.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-[#718096] mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(blog.created_at)}
                    </div>
                    <div className="flex items-center gap-1">
                      <FileText size={14} />
                      {blog.read_time}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#6366F1] transition-colors">
                    {blog.title}
                  </h3>
                  <p className="body-standard text-[#A0AEC0] mb-4">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-[#6366F1] text-sm font-semibold group-hover:gap-3 transition-all">
                    Read Article
                    <ArrowRight size={16} />
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#161B26] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#6366F1] rounded-full filter blur-3xl opacity-10"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#EC4899] rounded-full filter blur-3xl opacity-10"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h2 className="heading-large select-none text-white mb-6">
              Ready to Turn <span className="text-[#6366F1]">Insight Into Action?</span>
            </h2>
            <p className="body-large text-[#A0AEC0] mb-8">
              Tools are only half the equation. Let's map real opportunities and build systems that actually work.
            </p>
            <a
              href="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#6366F1] hover:bg-[#5558E6] text-white font-semibold text-lg rounded-full transition-colors"
            >
                Start a Free Assessment
                <ArrowRight size={20} />
            </a>
          </motion.div>
        </div>
      </section>
      <Footer />
      
      {/* DownloadSimple Modal */}
      <EbookDownloadModal
        isOpen={showDownloadModal}
        ebook={selectedEbook}
        onClose={() => {
          setShowDownloadModal(false);
          setSelectedEbook(null);
        }}
      />
    </div>
  );
};

export default Resources;
